/**
 * TrackingLink Component
 *
 * A drop-in replacement for <Link> and <a> tags that automatically
 * adds UTM tracking parameters for internal links.
 *
 * Features:
 * - Auto-detects internal vs external links
 * - Merges with existing UTM params (configurable)
 * - Context-aware defaults (blog, admin, website)
 * - Opt-out flag for skip-tracking scenarios
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  buildTrackingUrl,
  buildUrlFromPreset,
  shouldAddTracking,
  type UTMParams,
  type UTMPreset,
  type BuildUrlOptions,
} from './utm-builder';

// ============================================================================
// Types
// ============================================================================

export interface TrackingLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /** The destination URL */
  href: string;
  /** Campaign name for attribution */
  campaign?: string;
  /** Content identifier (e.g., button location) */
  content?: string;
  /** Use a preset configuration */
  preset?: UTMPreset;
  /** Custom UTM params (merged with preset if both provided) */
  utmParams?: Partial<UTMParams>;
  /** Skip adding UTM params entirely */
  skipTracking?: boolean;
  /** Preserve existing UTM params in URL (default: false) */
  preserveExisting?: boolean;
  /** Children elements */
  children: React.ReactNode;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Detect the context (blog, admin, website) based on current URL
 */
function detectContext(): 'blog' | 'admin' | 'website' {
  if (typeof window === 'undefined') return 'website';

  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  if (hostname.startsWith('admin.')) return 'admin';
  if (pathname.startsWith('/blog')) return 'blog';
  return 'website';
}

/**
 * Get default UTM params based on context
 */
function getContextDefaults(context: 'blog' | 'admin' | 'website'): Partial<UTMParams> {
  switch (context) {
    case 'blog':
      return { utm_source: 'blog', utm_medium: 'link' };
    case 'admin':
      return { utm_source: 'admin', utm_medium: 'link' };
    default:
      return { utm_source: 'website', utm_medium: 'cta' };
  }
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * TrackingLink - Universal tracking-enabled link component
 *
 * @example
 * ```tsx
 * // Simple usage with auto-detected context
 * <TrackingLink href="/pricing" campaign="homepage">
 *   View Pricing
 * </TrackingLink>
 *
 * // With preset
 * <TrackingLink href="/signup" preset="cta_hero">
 *   Get Started
 * </TrackingLink>
 *
 * // Skip tracking for certain links
 * <TrackingLink href="/settings" skipTracking>
 *   Settings
 * </TrackingLink>
 * ```
 */
export function TrackingLink({
  href,
  campaign,
  content,
  preset,
  utmParams,
  skipTracking = false,
  preserveExisting = false,
  children,
  ...anchorProps
}: TrackingLinkProps) {
  // Determine if we should add tracking
  const shouldTrack = !skipTracking && shouldAddTracking(href);

  // Build the final URL
  let finalUrl = href;

  if (shouldTrack) {
    const context = detectContext();
    const contextDefaults = getContextDefaults(context);

    // Build params: context defaults < preset < custom params < campaign/content
    let params: Partial<UTMParams> = { ...contextDefaults };

    if (preset) {
      // Use preset as base, merge with context defaults
      const presetUrl = buildUrlFromPreset(href, preset, {}, { preserveExisting });
      // Extract just the params from preset for merging
      const url = new URL(presetUrl, window.location.origin);
      url.searchParams.forEach((value, key) => {
        if (key.startsWith('utm_')) {
          params[key as keyof UTMParams] = value;
        }
      });
    }

    if (utmParams) {
      params = { ...params, ...utmParams };
    }

    // Apply campaign and content if provided
    if (campaign) {
      params.utm_campaign = campaign;
    }
    if (content) {
      params.utm_content = content;
    }

    const options: BuildUrlOptions = {
      preserveExisting,
      skipTracking: false,
    };

    finalUrl = buildTrackingUrl(href, params, options);
  }

  // Check if this is an internal link (use React Router) or external (use <a>)
  const isInternal = href.startsWith('/') || href.startsWith(window?.location?.origin || '');
  const isExternal = href.startsWith('http') && !isInternal;

  if (isExternal) {
    return (
      <a href={finalUrl} {...anchorProps}>
        {children}
      </a>
    );
  }

  // For internal links, use React Router's Link
  // Extract just the pathname + search from the full URL
  let to = finalUrl;
  if (finalUrl.startsWith('http')) {
    try {
      const url = new URL(finalUrl);
      to = url.pathname + url.search + url.hash;
    } catch {
      to = finalUrl;
    }
  }

  return (
    <Link to={to} {...anchorProps}>
      {children}
    </Link>
  );
}

// ============================================================================
// Convenience Components
// ============================================================================

/**
 * Pre-configured link for email campaigns
 */
export function EmailLink({
  href,
  campaign,
  emailType = 'campaign',
  content,
  children,
  ...props
}: Omit<TrackingLinkProps, 'preset'> & {
  emailType?: 'campaign' | 'sequence' | 'transactional';
}) {
  const presetMap = {
    campaign: 'email_campaign',
    sequence: 'email_sequence',
    transactional: 'email_transactional',
  } as const;

  return (
    <TrackingLink
      href={href}
      preset={presetMap[emailType]}
      campaign={campaign}
      content={content}
      {...props}
    >
      {children}
    </TrackingLink>
  );
}

/**
 * Pre-configured link for blog posts
 */
export function BlogLink({
  href,
  postSlug,
  isCta = false,
  children,
  ...props
}: Omit<TrackingLinkProps, 'preset' | 'content' | 'campaign'> & {
  postSlug: string;
  isCta?: boolean;
}) {
  return (
    <TrackingLink
      href={href}
      preset={isCta ? 'blog_cta' : 'blog_internal'}
      campaign="blog_content"
      content={postSlug}
      {...props}
    >
      {children}
    </TrackingLink>
  );
}

/**
 * Pre-configured link for CTAs
 */
export function CTALink({
  href,
  location,
  campaign,
  children,
  ...props
}: Omit<TrackingLinkProps, 'preset' | 'content'> & {
  location: 'hero' | 'pricing' | 'navbar' | 'footer' | string;
}) {
  const presetMap: Record<string, UTMPreset> = {
    hero: 'cta_hero',
    pricing: 'cta_pricing',
    navbar: 'cta_navbar',
    footer: 'cta_footer',
  };

  const preset = presetMap[location];

  return (
    <TrackingLink
      href={href}
      preset={preset}
      content={preset ? undefined : location}
      campaign={campaign}
      {...props}
    >
      {children}
    </TrackingLink>
  );
}

export default TrackingLink;
